/* eslint-disable object-curly-newline */
import { useState, useEffect } from 'preact/hooks';
import PropTypes from 'prop-types';
import AppState from '@state';
import { Remarkable } from 'remarkable';

import { getOrgContractDownload, deleteOrgContract, postOrgContractQuery, postOrgContractExport } from '@api/restricted/org-contracts-api';

import AlertMessage from '@ui-kit/alert/Alert';
import BaseButton from '@ui-kit/buttons/baseButton';
import BreakLine from '@ui-kit/dividers/breakLine';
import Header from '@ui-kit/typography/header';
import Tabs from '@ui-kit/tabs/Tabs';
import Box from '@ui-kit/box';
import AiSummary from '@distinct-components/dataDisplay/aiSummary/AiSummary';
import Icon from '@ui-kit/icon';
import AiIcon from '@assets/icons/ai-icon.svg';
import SearchIcon from '@assets/icons/search-list.svg';
import CloseIcon from '@assets/icons/close-square.svg';
import PrimaryText from '@ui-kit/typography/primaryText';
import Spinner from '@ui-kit/loaders/Spinner';

import SearchInput from '@ui-kit/inputs/searchInput';

import BaseEditOverlay from '@layouts/full-screen/baseEditOverlay';
import ConfirmDeleteUtilityBtn from '@components/distinct-components/distinctButtons/confirmDeleteUtilityBtn/ConfirmDeleteUtilityBtn';

import AddedContractDataView from '../publisherAddContract/sections/reviewAddedContract/sections/addedContractDataView/AddedContractDataView';

function PublisherViewContract({ contract, loadContracts, closeFunction }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [queryError, setQueryError] = useState(null);
  const [currentTab, setCurrentTab] = useState('summary');

  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [currentAiQuery, setCurrentAiQuery] = useState('');
  const [newAiQuery, setNewAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiResponseText, setAiResponseText] = useState('');
  const [aiConversationHistory, setAiConversationHistory] = useState([]);
  const [loadingText, setLoadingText] = useState('Reviewing Document...');

  const md = new Remarkable();

  const useToastAlert = (message, type) => {
    AppState.messages.toastMessage.value = { text: message, variant: type };
  };

  useEffect(() => {
    if (!isAwaitingResponse) return () => { };

    const messages = ['Analyzing Document...', 'Reasoning...'];
    let timeoutId = null;
    setLoadingText(messages[0]);

    const randomDelay = Math.floor(Math.random() * (4000 - 1500 + 1)) + 1500;
    timeoutId = setTimeout(() => {
      setLoadingText(messages[1]);
    }, randomDelay);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAwaitingResponse]);

  const handleDownloadContract = () => {
    setError(null);
    setIsDownloading(true);

    getOrgContractDownload(AppState.pubOrganization.id.value, contract.id)
      .then((response) => {
        if (response.status !== 200) {
          response.json()
            .then((json) => setError(json.error || response.statusText))
            .catch(() => setError(response.statusText));
          setIsDownloading(false);
        } else {
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = contract.fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setIsDownloading(false);
          });
        }
      })
      .catch((err) => {
        setIsDownloading(false);
        setError(err.message);
      });
  };

  const handleExportContract = () => {
    setError(null);
    setIsExporting(true);

    postOrgContractExport(AppState.pubOrganization.id.value, contract.id)
      .then((response) => {
        if (response.status !== 200) {
          response.json()
            .then((json) => useToastAlert(json.error || response.statusText, 'error'))
            .catch(() => useToastAlert(response.statusText, 'error'));
          setIsExporting(false);
        } else {
          useToastAlert('Successfully Exported to Airtable', 'success');
          setIsExporting(false);
        }
      })
      .catch((err) => {
        setIsExporting(false);
        useToastAlert(err.message, 'error');
      });
  };

  const handleDeleteContract = () => {
    setError(null);
    setIsDeleting(true);

    deleteOrgContract(AppState.pubOrganization.id.value, contract.id)
      .then((response) => {
        if (response.status !== 200) {
          response.json()
            .then((json) => setError(json.error || response.statusText))
            .catch(() => setError(response.statusText));
          setIsDeleting(false);
        } else {
          loadContracts().then(() => {
            setIsDeleting(false);
            closeFunction();
          });
        }
      })
      .catch((err) => {
        setIsDeleting(false);
        setError(err.message);
      });
  };

  const handleTabChange = (tab) => {
    setError(null);
    setCurrentTab(tab.value);
  };

  const streamResponse = (fullText) => {
    const words = fullText.split(' ');
    let index = 0;
    let lastTime = null;

    setAiResponseText(''); // Reset display

    const animate = (timestamp) => {
      if (!lastTime) lastTime = timestamp;
      const elapsed = timestamp - lastTime;

      if (elapsed > 50) {
        if (index < words.length) {
          setAiResponseText((prev) => `${prev}${words[index]} `);
          index += 1;
          lastTime = timestamp;
        } else {
          return; // Stop animation
        }
      }
      requestAnimationFrame(animate);
    };

    const animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId); // Cleanup
  };

  const handleContractAIQuery = () => {
    if (!(newAiQuery?.trim()?.length > 0)) {
      setError('A response cannot be generated for your question');
      return;
    }

    setQueryError(null);
    setAiResponse('');
    setCurrentAiQuery('');
    setIsAwaitingResponse(true);

    postOrgContractQuery(AppState.pubOrganization.id.value, contract.data.documentText, newAiQuery.trim(), aiConversationHistory)
      .then((response) => {
        if (response.status !== 200) {
          response.json()
            .then((json) => setQueryError(json.error || response.statusText))
            .catch(() => setQueryError(response.statusText));
          setIsAwaitingResponse(false);
        } else {
          response.json().then((json) => {
            const fullAnswer = json.answer;
            const exchange = { user: newAiQuery, agent: fullAnswer };
            setAiConversationHistory((prev) => [...prev, exchange]);
            setAiResponse(fullAnswer);
            setCurrentAiQuery(newAiQuery.trim());
            setNewAiQuery('');
            setIsAwaitingResponse(false);
            // Mock the stream response
            streamResponse(fullAnswer);
          });
        }
      })
      .catch((err) => {
        setIsAwaitingResponse(false);
        setError(err.message);
      });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleContractAIQuery();
    }
  };

  const onClickClearResponse = () => {
    setAiResponse('');
  };

  return (
    <BaseEditOverlay formWidth="46rem" closeFunction={closeFunction}>
      <Header mb="0.75rem">{contract.name}</Header>

      <Box mt="1rem" mb="1.25rem">
        <Tabs
          tabs={[
            { name: 'AI Summary', value: 'summary' },
            { name: 'Standard Fields', value: 'fields' },
          ]}
          handleTabChange={handleTabChange}
          selectedTab={currentTab}
        />
      </Box>

      <BreakLine mb="1.5em" color="var(--panel-info-border)" />

      {currentTab === 'summary' && (
        <Box>
          <AiSummary
            summary={contract.summary || 'Unable to create summary information'}
          />

          <Box className="contract-ai-query">
            <Box className="input-wrapper">
              <SearchInput
                value={newAiQuery}
                onChange={(e) => setNewAiQuery(e.target.value)}
                disabled={isAwaitingResponse}
                onKeyDown={handleKeyDown}
                maxLength="200"
                placeholder="Ask anything about this contract..."
                fluid
              />
              <Box className="search-btn" onClick={handleContractAIQuery}>
                <Icon color="var(--text-primary)" size="1rem" mr="0.5rem" cursor>
                  <SearchIcon />
                </Icon>
              </Box>
            </Box>

            {isAwaitingResponse && !queryError && (
              <Box display="flex" alignItems="center" mt="0.875rem" ml="0.25rem">
                <Spinner mono size="1.125em" width="2px" mr="0.5rem" />
                <PrimaryText className="thinking">{loadingText}</PrimaryText>
              </Box>
            )}

            {queryError && (
              <AlertMessage
                variant="warning"
                message="Unable to find answer to this query. Try again."
                mt="0.75em"
              />
            )}

            {aiResponse && (
              <Box className="response-wrapper">
                <Box className="clear-btn" onClick={onClickClearResponse}>
                  <Icon color="var(--text-primary)" size="1.25rem" cursor>
                    <CloseIcon />
                  </Icon>
                </Box>
                <Box className="response-icon">
                  <Icon color="var(--text-primary)" size="0.875rem">
                    <AiIcon />
                  </Icon>
                </Box>
                <Box color="var(--text-primary)" dangerouslySetInnerHTML={{ __html: md.render(aiResponseText) }} />
              </Box>
            )}
          </Box>

          <Box className="contract-ai-markdown" mb="1.5rem" mt="1.5rem">
            <Box display="flex" alignItems="center" mb="0.5rem">
              <Icon color="var(--text-primary)" size="0.875rem" mr="0.5rem">
                <AiIcon />
              </Icon>
              <PrimaryText color="var(--text-primary)" lineHeight="1">Full Summary</PrimaryText>
            </Box>
            <Box dangerouslySetInnerHTML={{ __html: md.render(contract.data.fullSummary) }} />
          </Box>
        </Box>
      )}

      {currentTab === 'fields' && (
        <AddedContractDataView
          contractSummary={contract.summary}
          contractData={contract.data}
          loadContracts={loadContracts}
          closeFunction={closeFunction}
          readOnly
        />
      )}

      {currentTab === 'query' && (
        <Box>

          {newAiQuery?.trim()?.length > 0 && (
            <BaseButton
              fluid
              mb="1em"
              maxHeight="2em"
              btnText="Submit"
              disabled={isAwaitingResponse}
              isLoading={isAwaitingResponse}
              onClick={handleContractAIQuery}
              onMouseDown={(e) => e.preventDefault()}
            />
          )}

          {aiResponse && (
            <Box className="contract-ai-markdown" mb="1.5rem" mt="1.5rem">
              <PrimaryText color="var(--text-primary)" lineHeight="1" mb="1.5rem">{currentAiQuery}</PrimaryText>
              <Box display="flex" alignItems="center" mb="0.5rem">
                <Icon color="var(--text-primary)" size="0.875rem" mr="0.5rem">
                  <AiIcon />
                </Icon>
                <PrimaryText color="var(--text-primary)" lineHeight="1">AI Response</PrimaryText>
              </Box>
              <Box dangerouslySetInnerHTML={{ __html: md.render(aiResponse) }} />
            </Box>
          )}
        </Box>
      )}

      {error && (
        <AlertMessage
          variant="negative"
          message={error}
          mb="1.5em"
          mt="1.5em"
        />
      )}

      {currentTab !== 'query' && (
        <BaseButton
          mb="1.5em"
          btnText="Download Original Contract"
          fluid
          isLoading={isDownloading}
          onClick={handleDownloadContract}
        />
      )}

      {currentTab !== 'query' && (
        <BaseButton
          mb="1.5em"
          btnText="Export to Airtable"
          fluid
          isLoading={isExporting}
          onClick={handleExportContract}
        />
      )}

      {currentTab !== 'query' && (
        <ConfirmDeleteUtilityBtn
          destructiveFunction={handleDeleteContract}
          isPerformingDestructiveAction={isDeleting}
          isTransparent
        />
      )}
    </BaseEditOverlay>
  );
}

PublisherViewContract.propTypes = {
  contract: PropTypes.object.isRequired,
  loadContracts: PropTypes.func.isRequired,
  closeFunction: PropTypes.func.isRequired,
};

export default PublisherViewContract;
